import React, { useState, useEffect } from 'react';
import { 
    EnvelopeIcon, 
    ChatBubbleBottomCenterTextIcon,
    ChevronUpIcon,
    ChevronDownIcon
} from '@heroicons/react/24/solid';
import UrlConfig from '../../data/UrlConfig';
import { useLocation } from 'react-router-dom';

const ContactSection = () => {
    const location = useLocation();
    const [email, setEmail] = useState('');
    const [message, setMessage] = useState('');
    
    // Estados para manejar el feedback visual del envío
    const [status, setStatus] = useState('idle'); // 'idle' | 'loading' | 'success' | 'error'
    const[errorMessage, setErrorMessage] = useState('');

    // Estado para manejar si está colapsado o no
    const[isCollapsed, setIsCollapsed] = useState(true);

    // Efecto para leer el estado del localStorage cuando cambia la ruta
    useEffect(() => {
        try {
            const storageKey = `contact_collapsed_${location.pathname}`;
            const storedState = localStorage.getItem(storageKey);
            
            if (storedState !== null) {
                // Si existe un estado guardado para esta ruta, lo usamos
                setIsCollapsed(JSON.parse(storedState));
            } else {
                // Estado por defecto (false = expandido)
                setIsCollapsed(true);
            }
        } catch (error) {
            console.error("Error leyendo del localStorage", error);
        }
    }, [location.pathname]);

    // Función para alternar el colapso y guardarlo en localStorage
    const handleToggleCollapse = () => {
        const newState = !isCollapsed;
        setIsCollapsed(newState);
        try {
            const storageKey = `contact_collapsed_${location.pathname}`;
            localStorage.setItem(storageKey, JSON.stringify(newState));
        } catch (error) {
            console.error("Error guardando en localStorage", error);
        }
    };

    const handleEmailSubmit = async (e) => {
        e.preventDefault();
        setStatus('loading');
        setErrorMessage('');
        
        try {
            const response = await fetch(UrlConfig.leads_action, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    email,
                    message: message,
                    nick: 'no-name-registered',
                    page: location.pathname,
                    folder: location.pathname,
                    status: 'consulting',
                    newsletter: [location.pathname],
                }),
            });

            if (!response.ok) {
                throw new Error(`Error en el servidor: ${response.status}`);
            }

            const data = await response.json();
            
            setStatus('success');
            setEmail('');
            setMessage('');
            
            setTimeout(() => setStatus('idle'), 5000);

        } catch (error) {
            console.error("Error al enviar el formulario:", error);
            setStatus('error');
            setErrorMessage('Hubo un problema al enviar tu mensaje. Por favor, intenta nuevamente.');
        }
    };

    return (
        <div className="mt-12 p-6 rounded-xl bg-gray-800/60 border border-gray-700 animate-fade-in transition-all duration-300">
            {/* Cabecera Clickable para colapsar/expandir */}
            <div 
                className="flex items-center justify-between cursor-pointer group"
                onClick={handleToggleCollapse}
                title={isCollapsed ? "Expandir sección de contacto" : "Colapsar sección de contacto"}
            >
                <h2 className="text-2xl font-semibold text-yellow-400 flex items-center gap-3 transition-colors group-hover:text-yellow-300">
                    <ChatBubbleBottomCenterTextIcon className="w-7 h-7" />
                    ¿Tienes dudas? Contáctame
                </h2>
                <button className="text-yellow-400 group-hover:text-yellow-300 transition-colors focus:outline-none">
                    {isCollapsed ? (
                        <ChevronDownIcon className="w-6 h-6" />
                    ) : (
                        <ChevronUpIcon className="w-6 h-6" />
                    )}
                </button>
            </div>
            
            {/* Contenedor del contenido (se oculta si isCollapsed es true) */}
            {!isCollapsed && (
                <div className="mt-6 flex flex-col md:flex-row gap-8 items-center justify-center animate-fade-in">
                    {/* Botón de WhatsApp */}
                    <div className="w-full md:w-1/2 flex flex-col items-center">
                        <p className="text-gray-300 mb-4 text-center text-sm">Escríbeme directamente por WhatsApp para una respuesta rápida.</p>
                        <a 
                            href="https://wa.me/5491150254270?text=Hola,%20tengo%20una%20duda%20sobre%20este%20tema:" 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="inline-flex items-center justify-center gap-2 bg-green-500 hover:bg-green-600 text-white font-bold py-3 px-6 rounded-lg transition-all duration-300 transform hover:scale-105 w-full sm:w-auto shadow-lg"
                        >
                            <svg className="w-6 h-6 fill-current" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                            </svg>
                            Escribir por WhatsApp
                        </a>
                    </div>

                    {/* Divisores dependiendo de la pantalla (Desktop/Mobile) */}
                    <div className="hidden md:block w-px h-32 bg-gray-600"></div>
                    <div className="block md:hidden w-full h-px bg-gray-600 my-2"></div>

                    {/* Formulario de Email */}
                    <div className="w-full md:w-1/2">
                        <p className="text-gray-300 mb-4 text-center text-sm">O si prefieres, envíame un mensaje por correo:</p>
                        <form onSubmit={handleEmailSubmit} className="space-y-3 relative">
                            <input 
                                type="email" 
                                placeholder="Tu correo electrónico" 
                                required 
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                disabled={status === 'loading'}
                                className="w-full p-2.5 bg-gray-700 text-white rounded-lg border border-gray-600 focus:ring-2 focus:ring-purple-500 focus:border-purple-500 placeholder-gray-400 disabled:opacity-50"
                            />
                            <textarea 
                                placeholder="Escribe tu mensaje o duda aquí..." 
                                required 
                                rows="3"
                                value={message}
                                onChange={(e) => setMessage(e.target.value)}
                                disabled={status === 'loading'}
                                className="w-full p-2.5 bg-gray-700 text-white rounded-lg border border-gray-600 focus:ring-2 focus:ring-purple-500 focus:border-purple-500 placeholder-gray-400 resize-none disabled:opacity-50"
                            ></textarea>
                            
                            <button 
                                type="submit" 
                                disabled={status === 'loading'}
                                className="w-full inline-flex items-center justify-center gap-2 bg-purple-600 hover:bg-purple-700 disabled:bg-purple-800 text-white font-bold py-2.5 px-4 rounded-lg transition-all duration-300 shadow-lg"
                            >
                                <EnvelopeIcon className="w-5 h-5"/>
                                {status === 'loading' ? 'Enviando...' : 'Enviar Mensaje'}
                            </button>

                            {/* Mensajes de Feedback */}
                            {status === 'success' && (
                                <p className="text-green-400 text-sm text-center mt-2 animate-fade-in">
                                    ¡Mensaje enviado con éxito!
                                </p>
                            )}
                            {status === 'error' && (
                                <p className="text-red-400 text-sm text-center mt-2 animate-fade-in">
                                    {errorMessage}
                                </p>
                            )}
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ContactSection;